import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Summary } from '../../../types/task.types'
import { useAudioEngine } from '../../../components/core-audio-ui-unified/use-audio-engine'
import { off, on, trigger } from '../../../lib/events'
import { useOnce } from '../../misc/use-once'
import { useStoreTimeline } from '../use-timeline/store'
import { KEY_STORAGE, getLastFeatureInteracted, mergeDefined } from './utils'

export interface ChannelSettings {
  id: string
  pan?: number
  volume?: number
  realVolume?: number
  isSolo?: boolean
  isMuted?: boolean
}

interface PanelsState {
  showLyrics?: boolean
  showChords?: boolean
  showSections?: boolean
}

export interface OperationSettings {
  type: string
  channels: ChannelSettings[]
  general?: GeneralState
  panels?: PanelsState
}

interface StorageSettings {
  id: string
  updatedAt: number
  channels?: any // DEPRECATED
  operations?: OperationSettings[]
}

export type GetSettings = (initial?: boolean) => OperationSettings | undefined

export interface UsePersistedSettings {
  onChange(data: any): void
  onGetSettings: GetSettings
  update(settings: Omit<OperationSettings, 'type'>): void
}

interface UsePersistedSettingsProps {
  trackId?: string
  operationType?: any
  watchChanges?: boolean
  summary?: Summary
}

interface GeneralState {
  masterVolume?: number
  playbackRate?: number
  pitchShiftCents?: number
  isRepeating?: boolean
  bpm?: string | number
  key?: string | number
  tempo?: string | number
  semitones?: string | number
  newTuning?: string | number
  originalTuning?: number | null
  typeChords?: string | null
  countIn?: {
    count: number
    enabled: boolean
  }
  loop?: {
    isLooping?: boolean
    fromMs?: number
    toMs?: number
  }
  lastFeatureInteracted?: string | undefined
}

export const usePersistedChannelSettings = ({
  trackId,
  operationType,
  watchChanges = true,
  summary
}: UsePersistedSettingsProps): UsePersistedSettings => {
  // Immutable initial state of the storage. Needed because the storage itself
  // may change during the player start up, overwriting its initial state
  const initial = useRef<StorageSettings[]>([])
  const storage = useRef<StorageSettings[]>([])

  useOnce(() => {
    const item = localStorage.getItem(KEY_STORAGE)

    if (!item) {
      localStorage.setItem(KEY_STORAGE, JSON.stringify([]))
      initial.current = []
      storage.current = []
    } else {
      initial.current = JSON.parse(item)
      storage.current = JSON.parse(item)
    }
  })

  const masterVolume = useAudioEngine((p) => p.state.masterVolume)
  const isRepeating = useAudioEngine((p) => p.state.isRepeating)
  const isLooping = useAudioEngine((p) => p.state.isLooping)
  const channelsState = useAudioEngine((p) =>
    Object.entries(p.channels).map(([key]) => ({
      id: p.channels[key].id,
      pan: p.channels[key].pan,
      volume: p.channels[key].volume,
      isSolo: p.channels[key].isSolo,
      isMuted: p.channels[key].isMuted,
      playbackRate: p.channels[key].playbackRate,
      pitchShiftCents: p.channels[key].pitchShiftCents
    }))
  )

  const rangeMs = useStoreTimeline((state) => state.rangeMs)

  /**
   * @deprecated
   */
  const onSetSettings = useCallback(
    (
      id: string,
      typeOperation: string,
      settings: ChannelSettings[],
      generalState: GeneralState,
      panels: PanelsState
    ) => {
      const trackSettings = storage.current.find(
        (i: StorageSettings) => i.id === id
      )
      const preset = {
        type: typeOperation,
        channels: settings,
        general: generalState,
        panels
      }

      if (trackSettings) {
        if (trackSettings?.operations) {
          const operationSettings = trackSettings.operations?.findIndex(
            (i: OperationSettings) => i.type === typeOperation
          )

          if (operationSettings >= 0) {
            trackSettings.operations[operationSettings] = preset
          } else {
            trackSettings.operations.push(preset)
          }
        } else {
          trackSettings.operations = []
          trackSettings.operations.push(preset)
        }

        trackSettings.channels = null // clean property deprecated
        trackSettings.updatedAt = Date.now()
        const trackSettingsIndex = storage.current.findIndex(
          (i: StorageSettings) => i.id === id
        )
        storage.current[trackSettingsIndex] = trackSettings
      } else {
        storage.current.push({
          id,
          updatedAt: Date.now(),
          operations: [preset]
        })
      }

      localStorage.setItem(KEY_STORAGE, JSON.stringify(storage.current))
    },
    []
  )

  const defineInitialSettings = useCallback(
    (_type: string): OperationSettings | undefined => {
      let type = _type

      if (type === 'custom-music-stems') {
        type = summary?.separate?.stems?.map((i) => i.stem).join('-') as string
      }

      const stateInitialChannels = type?.split('-').map((i: any) => ({
        id: i,
        pan: 0,
        volume: 0.75,
        isSolo: false,
        isMuted: false
      }))

      if (stateInitialChannels) {
        ;['metronome', 'metronome_half', 'metronome_double'].forEach((i) => {
          stateInitialChannels.push({
            id: i,
            pan: 0,
            volume: 0.75,
            isSolo: false,
            isMuted: true
          })
        })

        return {
          type,
          channels: stateInitialChannels
        }
      }

      return undefined
    },
    [summary]
  )

  const onGetSettings = useCallback<GetSettings>(
    (isInitial = false) => {
      const state = (isInitial ? initial.current : storage.current)
        .find((i: StorageSettings) => i.id === trackId)
        ?.operations?.find(
          (i: OperationSettings) => i.type === operationType
        ) as OperationSettings

      if (state) {
        return state
      }

      return defineInitialSettings(operationType)
    },
    [trackId, operationType, defineInitialSettings]
  )

  const getPanelsSettings = useCallback(
    (settingsPreset: OperationSettings | undefined, detail: any) => {
      const showLyrics = detail?.panels?.showSections
        ? false
        : detail?.panels?.showLyrics !== undefined
        ? detail?.panels?.showLyrics
        : settingsPreset?.panels?.showLyrics || false
      const showChords = detail?.panels?.showSections
        ? false
        : detail?.panels?.showChords !== undefined
        ? detail?.panels?.showChords
        : settingsPreset?.panels?.showChords || false
      const showSections =
        showLyrics || showChords
          ? false
          : detail?.panels?.showSections !== undefined
          ? detail?.panels?.showSections
          : settingsPreset?.panels?.showSections || false

      return {
        showLyrics,
        showChords,
        showSections
      }
    },
    []
  )

  const onDebouncedChange = useDebouncedCallback(
    (detail: any): void => {
      if (trackId && operationType && channelsState?.length) {
        const settingsPreset = onGetSettings()
        const generalState = {
          masterVolume,
          loop: { fromMs: rangeMs[0], toMs: rangeMs[1], isLooping },
          isRepeating,
          typeChords: detail?.typeChords || settingsPreset?.general?.typeChords,
          countIn: detail?.presetCountIn || settingsPreset?.general?.countIn,
          playbackRate: (channelsState[0]?.playbackRate ?? 1) * 100,
          pitchShiftCents: channelsState[0]?.pitchShiftCents ?? 0
        }
        const panels = getPanelsSettings(settingsPreset, detail)

        onSetSettings(
          trackId,
          operationType,
          channelsState,
          generalState,
          panels
        )
        trigger('player:channel-changed-2')
      }
    },
    1000,
    { trailing: true, maxWait: 2000 }
  )

  /**
   * @deprecated
   */
  const onChange = useCallback(
    ({ detail }: any): void => {
      const settings = onGetSettings()

      onDebouncedChange(detail)

      // Flush immediately if there isn't persisted settings

      if (!settings || !settings.general) {
        onDebouncedChange.flush()
        return
      }

      // Otherwise, persist only the ones that depends on the detail params to
      // prevent loosing on the debouncing. The rest will be persisted in the
      // debounced function call

      settings.general.countIn =
        detail?.presetCountIn ?? settings.general.countIn
      settings.panels = getPanelsSettings(settings, detail)
    },
    [getPanelsSettings, onDebouncedChange, onGetSettings]
  )

  const update = useCallback(
    (data: Omit<OperationSettings, 'type'>) => {
      if (!trackId) {
        return
      }

      const current = onGetSettings()
      const preset: OperationSettings = {
        type: operationType,
        channels: data.channels,
        general: mergeDefined(current?.general, data.general),
        panels: mergeDefined(current?.panels, data.panels)
      }

      const lastFeatureInteracted = getLastFeatureInteracted(current, data)
      if (preset?.general) {
        preset.general.lastFeatureInteracted = lastFeatureInteracted
      }

      const trackSettingsIndex = storage.current.findIndex(
        (track: StorageSettings) => track.id === trackId
      )
      const trackSettings = storage.current[trackSettingsIndex]

      if (trackSettings) {
        trackSettings.operations ??= []

        const opIndex = trackSettings.operations.findIndex(
          (op: OperationSettings) => op.type === operationType
        )

        if (opIndex >= 0) {
          trackSettings.operations[opIndex] = preset
        } else {
          trackSettings.operations.push(preset)
        }

        trackSettings.channels = null // clean property deprecated
        trackSettings.updatedAt = Date.now()
        storage.current[trackSettingsIndex] = trackSettings
      } else {
        storage.current.push({
          id: trackId,
          updatedAt: Date.now(),
          operations: [preset]
        })
      }

      localStorage.setItem(KEY_STORAGE, JSON.stringify(storage.current))
    },
    [onGetSettings, operationType, trackId]
  )

  useEffect(() => {
    if (!watchChanges) {
      return
    }

    on('player:channel-changed', onChange)

    // eslint-disable-next-line consistent-return
    return () => {
      onDebouncedChange.flush()
      off('player:channel-changed', onChange)
    }
  }, [onDebouncedChange, onChange, watchChanges])

  return useMemo(
    () => ({
      onGetSettings,
      onChange,
      update
    }),
    [onChange, onGetSettings, update]
  )
}
